/* eslint-disable @typescript-eslint/ban-ts-comment */
import axios, { AxiosError } from 'axios';
import { REFRESH_TOKEN, REQUEST_SELF_SIGNUP } from './paths';
import config from '@config';
import _startsWith from 'lodash/startsWith';
import { parseISO } from 'date-fns';
const axiosInstance = axios.create({
  baseURL: config.api
});

type RefreshPayload = {
  refreshToken: string;
  clientId?: string;
  email?: string;
};

let refreshAccessTokenPromise: Promise<string> | null = null;

const redirectToLogin = () => {
  const sellerToken = localStorage.getItem('sellerToken');
  if (sellerToken) {
    localStorage.removeItem('sellerToken');
    window.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { path: `/inspection/login#token=${sellerToken}` }
      })
    );
  } else {
    window.dispatchEvent(
      new CustomEvent('navigate', {
        detail: { path: '/login' }
      })
    );
  }
};

const buildRefreshPayload = (refreshToken: string): RefreshPayload => {
  const clientId = localStorage.getItem('clientId');
  const email = localStorage.getItem('email');
  const refreshPayload: RefreshPayload = {
    refreshToken
  };

  if (clientId) {
    refreshPayload.clientId = clientId;
  }

  // Email is required for mobile login, optional for desktop login.
  if (email) {
    refreshPayload.email = email;
  }

  return refreshPayload;
};

const requestAccessToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const refreshResponse = await post(
    REFRESH_TOKEN,
    buildRefreshPayload(refreshToken),
    false
  );
  localStorage.setItem('accessToken', refreshResponse.accessToken);
  localStorage.setItem('expiry', refreshResponse.expiry);
  return refreshResponse.accessToken;
};

// Redirects to login at most once per failed refresh: redirectToLogin consumes
// sellerToken, so a second call would send a seller to /login instead.
const refreshAccessToken = (): Promise<string> => {
  if (!refreshAccessTokenPromise) {
    refreshAccessTokenPromise = requestAccessToken()
      .catch((error) => {
        console.error('Error refreshing token:', error);
        redirectToLogin();
        throw error;
      })
      .finally(() => {
        refreshAccessTokenPromise = null;
      });
  }

  return refreshAccessTokenPromise;
};

axiosInstance.interceptors.request.use(
  async (config: any) => {
    config.metadata = { startTime: Date.now() };
    // Skip token handling for /api/token/ requests
    const skipUrls = [
      '/login',
      REQUEST_SELF_SIGNUP
    ];
    if (config.url.startsWith('/api/token/') || skipUrls.includes(config.url)) {
      return config;
    }

    let accessToken: any = localStorage.getItem('accessToken');
    const expiry = localStorage.getItem('expiry');
    const now = new Date();

    // Function to check if the token is expired
    const isTokenExpired = (expiry: string) => {
      const expiryDate = Number(expiry) || parseISO(expiry).getTime();
      return now.getTime() > expiryDate;
    };

    // If token is expired or missing, refresh it before making the request
    if (!accessToken || (expiry && isTokenExpired(expiry))) {
      accessToken = await refreshAccessToken();
    }

    // Set the Authorization header for all requests except login
    config.headers.Authorization = `Bearer ${accessToken}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const handleErrors = async (error: AxiosError) => {
  if (error.response) {
    window.onbeforeunload = null;
    if (
      error.response.status === 401 &&
      error.response.config.url !== REFRESH_TOKEN
    ) {
      try {
        await refreshAccessToken();
      } catch {
        return error;
      }
    }
    if (
      localStorage.getItem('accessToken') === null &&
      !_startsWith(error.request.url, '/api/token/')
    ) {
      redirectToLogin();
    }
    if (error.response.status === 500) {
      return error.response.data;
    }
    if (error.response.status === 422) {
      return error.response.data;
    }
    console.error('Response error:', error.response.data);
    //@ts-ignore
    if (error.response.data?.errors?.length > 0) {
      //@ts-ignore
      alert(error.response.data?.errors[0].message);
    }
  } else if (error.request) {
    console.error('Request error:', error.request);
  } else {
    console.error('Error:', error.message);
  }
  return error;
};

const get = (url: string, params = {}) => {
  return axiosInstance
    .get(url, { params })
    .then((response) => response.data)
    .catch(handleErrors);
};

// Modified post method
const post = (url: string, data = {}, useGlobalCatch = true, config = {}) => {
  const urlParams = new URLSearchParams(window.location.search);
  const apiDebug = urlParams.get('apiDebug');
  if (
    apiDebug &&
    url.indexOf(apiDebug) > -1 &&
    process.env.NODE_ENV === 'development'
  ) {
    console.log('post', url, data, config);
    return axiosInstance
      .post(url, data, config)
      .then((response) => {
        return Promise.reject({
          response: {
            status: 500,
            data: {
              errors: [{ message: 'Dummy API error for testing' }]
            }
          }
        });
      })
      // Use async here in case handleErrors returns a Promise (for example, if logging or UI alert is asynchronous).
      // This ensures thrown errors are fully handled before control is returned to the caller.
      .catch(async (error) => {
        if (useGlobalCatch) {
          throw await handleErrors(error);
        } else {
          throw error;
        }
      });
  }
  return axiosInstance
    .post(url, data, config)
    .then((response) => response.data)
    // Use async here in case handleErrors returns a Promise (for example, if logging or UI alert is asynchronous).
    .catch(async (error) => {
      if (useGlobalCatch) {
        throw await handleErrors(error);
      } else {
        throw error;
      }
    });
};

// Modified put method
const put = (url: string, data = {}) => {
  return axiosInstance
    .put(url, data)
    .then((response) => response.data)
    .catch(handleErrors);
};

const remove = (url: string) => {
  return axiosInstance
    .delete(url)
    .then((response) => response.data)
    .catch(handleErrors);
};

export { get, post, put, remove, refreshAccessToken };
